import React, { useState } from "react";
import "./HomePage.css";
import { executeCode } from "./api/pathfindingService";

const HomePage = () => {
  const [code, setCode] = useState({
    cpp: `#include <iostream>
#include <vector>
#include <queue>
#include <climits>
using namespace std;

#define INF INT_MAX

void dijkstra(vector<vector<pair<int, int>>> &graph, int source) {
  int V = graph.size();
  vector<int> dist(V, INF);
  priority_queue<pair<int, int>, vector<pair<int, int>>, greater<pair<int, int>>> pq;
  dist[source] = 0;
  pq.push({0, source});

  while (!pq.empty()) {
    int u = pq.top().second;
    pq.pop();

    for (auto &neighbor : graph[u]) {
      int v = neighbor.first;
      int weight = neighbor.second;

      if (dist[u] + weight < dist[v]) {
        dist[v] = dist[u] + weight;
        pq.push({dist[v], v});
      }
    }
  }

  // Print shortest distances from source to all vertices
  cout << "Shortest distances from source " << source << ":" << endl;
  for (int i = 0; i < V; ++i)
    cout << "Vertex " << i << ": " << dist[i] << endl;
}

int main() {
  int V = 5;
  vector<vector<pair<int, int>>> graph(V);

  // Example graph setup
  graph[0].push_back({1, 2});
  graph[0].push_back({2, 4});
  graph[1].push_back({2, 1});
  graph[1].push_back({3, 7});
  graph[2].push_back({3, 3});
  graph[3].push_back({4, 1});

  dijkstra(graph, 0);

  return 0;
}`,
  });

  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("cpp");

  const handleCodeChange = (e) => {
    setCode({ ...code, [selectedLanguage]: e.target.value });
  };

  // Prevent editing but allow selection and copying
  const handleKeyDown = (e) => {
    // Allow: Ctrl+C (copy), Ctrl+A (select all)
    const isCopy = (e.ctrlKey || e.metaKey) && e.key === 'c';
    const isSelectAll = (e.ctrlKey || e.metaKey) && e.key === 'a';
    
    if (isCopy || isSelectAll) {
      return; // Allow these actions
    }
    
    // Prevent all other keyboard input
    e.preventDefault();
  };

  // Prevent paste
  const handlePaste = (e) => {
    e.preventDefault();
  };

  // Prevent drag-drop
  const handleDrop = (e) => {
    e.preventDefault();
  };

  // Prevent cut
  const handleCut = (e) => {
    e.preventDefault();
  };

  const runCode = async () => {
    setLoading(true);
    try {
      const result = await executeCode(selectedLanguage, code[selectedLanguage]);

      if (result.status === "success") {
        setOutput(result.stdout);
      } else {
        setOutput(result.stderr || result.message);
      }
    } catch (error) {
      console.error("Error executing code:", error);
      setOutput(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="main-content">
      <section className="intro">
        <h2>What is Dijkstra's Algorithm?</h2>
        <p>
          Dijkstra's algorithm is a famous algorithm used for finding the
          shortest paths between nodes in a graph. It was conceived by computer
          scientist Edsger W. Dijkstra in 1956 and is widely used in various
          applications, including GPS navigation systems and network routing
          protocols.
        </p>
        <p>
          The algorithm works by iteratively selecting the vertex with the
          smallest known distance from the source vertex and updating the
          distances to its neighboring vertices. This process continues until
          the shortest path to all vertices is determined.
        </p>
      </section>
      <section className="code-editor">
        <h2>Code Editor</h2>
        <textarea
          className="code-input"
          value={code[selectedLanguage]}
          onChange={handleCodeChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onDrop={handleDrop}
          onCut={handleCut}
          readOnly
          placeholder="Enter your code here..."
        />
        <div className="language-select">
          <label htmlFor="language">Select Language:</label>
          <select
            id="language"
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
          >
            <option value="cpp">C++</option>
          </select>
        </div>
        <button onClick={runCode} disabled={loading}>
          {loading ? "Running..." : "Run Code"}
        </button>
      </section>
      <section className="output">
        <h2>Output</h2>
        <pre className="pretag">{output}</pre>
      </section>
      <section className="applications">
        <h2>Applications</h2>
        <p>
          Dijkstra's algorithm is used in various fields such as transportation
          networks, computer networks, and even in biology for analyzing
          metabolic pathways. Its efficiency and simplicity make it a
          fundamental algorithm in graph theory and computer science.
        </p>
      </section>
    </div>
  );
};

export default HomePage;
